import { Fragment, useState } from "react";
import { Popover } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

import {
  MAX_REMINDERS_PER_TASK,
  MESSAGE_KINDS,
  newReminderId,
  sortReminders,
} from "../../modules/administration/plan-management/components/reminder-mapping";
import ReminderTimePicker from "./reminder-time-picker";
import {
  describeReminder,
  describeReminderParts,
  isLeadTime,
  nextFreeLeadSlot,
  nextFreeOffset,
  nextFreeSlot,
} from "./reminder-time-format";
import "./reminder-time.css";

/**
 * LF360-1940 — the profile question a reminder counts from, when this subject
 * has not answered it.
 *
 * A relative reminder is only a reminder once there is an answer to count from.
 * The picker already refuses to offer such a field — it shows it disabled, with
 * the reason — but a reminder can arrive already set to one: inherited from the
 * task, where the choice was made for every subject at once and cannot be right
 * for all of them. Shown as an ordinary reminder it reads as configured and
 * working, and it is neither; it will start on the day they answer.
 *
 * Only where the caller knows the subject's answers. The plan panels pass fields
 * without them, nothing is marked disabled there, and no note appears.
 */
const unansweredFieldOf = (reminder, triggerFields = []) => {
  if (!reminder || reminder.notificationType !== "relative") { return null; }

  const field = (triggerFields || []).find(
    (option) => option.value === reminder.triggerSubjectProfileFieldId
  );

  return field && field.disabled ? field : null;
};

/**
 * LF360-1896 — a list of reminders, each one a phrase you can click.
 *
 * The row says what it does in words ("30 min before Wake up") and opens the
 * picker when clicked; the cross removes it. That replaces clearing a time picker
 * to delete a row, which worked but was not a thing anyone would guess.
 *
 * Storage is untouched: this edits the same list reminder-mapping already reads
 * out of an activity or the project's settings, so nothing downstream knows the
 * control changed.
 */
const ReminderList = ({
  value = [],
  onChange,
  readOnly = false,
  triggerFields = [],
  // Surfaces that cannot be left without one — the project's own reminder is
  // either off, above, or has a time.
  minimum = 0,
  popupContainerRef,
  addLabel = "Add Reminder",
  // LF360-1881: a triggered task can also be announced before it is assigned.
  // Only it — every other surface knows a task's date no earlier than the task
  // itself does.
  allowLeadTime = false,
  // LF360-1940: the earliest a fixed time may be, in minutes since local
  // midnight. Passed to every picker, and to the slot a new reminder lands on —
  // otherwise Add Reminder hands the admin a time the backend will refuse.
  minMinutes = null,
}) => {
  const reminders = Array.isArray(value) ? value : [];

  // Capacity is counted the way storage works rather than by row count. The
  // first reminder on the day goes into the activity's own columns and
  // everything else into extraReminders, which the backend caps at
  // MAX_REMINDERS_PER_TASK - 1. So ten rows are fine when one of them is on the
  // day, and one too many when they are all announcements.
  const EXTRAS_CAPACITY = MAX_REMINDERS_PER_TASK - 1;
  const onTheDay = reminders.filter((reminder) => !isLeadTime(reminder)).length;
  const announcements = reminders.length - onTheDay;
  const canAdd = reminders.length <= EXTRAS_CAPACITY;

  // Which row has its picker open. Held here so adding one can open its own
  // straight away: a new reminder lands at whatever free slot was available and
  // the list re-sorts around it, so on a list of several it is genuinely hard to
  // tell which row just appeared. Opening it answers that, and saves the click
  // that was going to follow anyway.
  const [openId, setOpenId] = useState(null);

  const getPopupContainer = popupContainerRef
    ? () => popupContainerRef.current || document.body
    : (trigger) => trigger.parentNode;

  const emit = (next) => {
    if (readOnly) return;
    onChange?.(sortReminders(next));
  };

  // Two reminders that resolve to the same moment are refused by the backend and
  // would only send the same push twice. An edit that would collide simply does
  // not take, the same rule the previous editor applied.
  // An announcement and a reminder on the day are never the same reminder, even
  // at the same hour: one is sent days earlier. The backend agrees, and keying
  // them the same way would refuse an edit it would have accepted.
  const signature = (reminder) => {
    if (isLeadTime(reminder)) {
      return `lead|${reminder.value ?? ""}|${reminder.unit ?? ""}|${reminder.remindHour ?? ""}|${reminder.remindMinute ?? ""}`;
    }

    return reminder.notificationType === "relative"
      ? `rel|${reminder.triggerSubjectProfileFieldId ?? ""}|${reminder.triggerOffsetMinutes ?? 0}`
      : `abs|${reminder.remindHour ?? ""}|${reminder.remindMinute ?? ""}`;
  };

  const patch = (id, changes) => {
    const current = reminders.find((reminder) => reminder.id === id);
    const others = reminders.filter((reminder) => reminder.id !== id);
    let changed = { ...current, ...changes };

    // Becoming an announcement gives up the base slot, and everything left has
    // to fit in the extras the backend accepts. A list already at the limit
    // cannot afford it, and the change is refused rather than saved and bounced.
    if (isLeadTime(changed) && !isLeadTime(current)) {
      const wouldUse = Math.max(0, onTheDay - 2) + announcements + 1;

      if (wouldUse > EXTRAS_CAPACITY) return;
    }

    if (others.some((r) => signature(r) === signature(changed))) {
      // Refusing a time the admin picked is honest: the row they clicked is
      // still there, showing the time it kept. Refusing a change of KIND is not
      // — nothing moves, and the control springs back with no reason given. So a
      // reminder changing kind lands somewhere free instead of nowhere.
      const kindOf = (reminder) => {
        if (isLeadTime(reminder)) return "lead";

        return reminder?.notificationType === "relative" ? "relative" : "absolute";
      };
      const was = kindOf(current);
      const becomes = kindOf(changed);

      if (was === becomes) return;

      // Becoming a time of day: the next free hour.
      if (becomes === "absolute") {
        const slot = nextFreeSlot(others, { minMinutes });

        // Every slot in the day taken: there is no time left to move it to.
        if (slot === null) return;

        changed = {
          ...changed,
          remindHour: Math.floor(slot / 60),
          remindMinute: slot % 60,
        };
      } else if (becomes === "relative") {
        // Becoming an offset: the next free one from the same answer. A second
        // reminder counting from the same field starts at zero like the first,
        // so on a list that already has one the tab did nothing at all — which
        // is the bug this mirrors, in the direction it was never fixed for.
        const offset = nextFreeOffset(others, changed.triggerSubjectProfileFieldId);

        if (offset === null) return;

        changed = { ...changed, triggerOffsetMinutes: offset };
      } else {
        // Becoming an announcement: keep the amount, which is the admin's whole
        // point, and move the time of day. It has to move rather than be
        // refused, because a reminder arriving from Relative brings no hour with
        // it — it is stored without one — so it lands on the same nine o'clock
        // every time and collides with any announcement already sent then.
        const slot = nextFreeLeadSlot(others, changed.value, changed.unit);

        if (slot === null) return;

        changed = {
          ...changed,
          remindHour: Math.floor(slot / 60),
          remindMinute: slot % 60,
        };
      }
    }

    emit(reminders.map((reminder) => (reminder.id === id ? changed : reminder)));
  };

  const remove = (id) => {
    // Otherwise the picker of a row that no longer exists stays behind.
    if (openId === id) setOpenId(null);
    emit(reminders.filter((reminder) => reminder.id !== id));
  };

  const add = () => {
    if (!canAdd) return;

    const slot = nextFreeSlot(reminders, { minMinutes });

    // null means every slot in the day is taken; nothing sensible to add.
    if (slot === null) return;

    const created = {
      id: newReminderId(),
      notificationType: "absolute",
      remindHour: Math.floor(slot / 60),
      remindMinute: slot % 60,
      messageKind: MESSAGE_KINDS.TASK_DUE,
    };

    emit([...reminders, created]);
    setOpenId(created.id);
  };

  return (
    <div className="reminder-list">
      {reminders.map((reminder) => {
        const phrase = describeReminder(reminder, triggerFields);
        const parts = describeReminderParts(reminder, triggerFields);
        const unanswered = unansweredFieldOf(reminder, triggerFields);

        return (
          <Fragment key={reminder.id}>
            <div className="reminder-list__row">
              <Popover
                open={!readOnly && openId === reminder.id}
                onOpenChange={(open) => setOpenId(open ? reminder.id : null)}
                trigger={readOnly ? [] : ["click"]}
                placement="bottomLeft"
                arrow={false}
                getPopupContainer={getPopupContainer}
                overlayClassName="reminder-time-popover"
                content={
                  <ReminderTimePicker
                    reminder={reminder}
                    triggerFields={triggerFields}
                    allowLeadTime={allowLeadTime}
                    minMinutes={minMinutes}
                    onChange={(changes) => patch(reminder.id, changes)}
                  />
                }
              >
                <button
                  type="button"
                  className="reminder-list__phrase"
                  disabled={readOnly}
                  data-testid="reminder-phrase"
                  // The two halves are one sentence; without this a screen reader
                  // reads the emphasis as a break in it.
                  aria-label={phrase}
                >
                  {parts.strong}
                  {parts.muted && (
                    <span className="reminder-list__phrase-muted"> {parts.muted}</span>
                  )}
                </button>
              </Popover>

              {!readOnly && reminders.length > minimum && (
                <button
                  type="button"
                  className="reminder-list__remove"
                  aria-label={`Remove reminder ${phrase}`}
                  onClick={() => remove(reminder.id)}
                >
                  <CloseOutlined />
                </button>
              )}
            </div>

            {unanswered && (
              <p className="reminder-list__inactive">
                {`Inactive until the subject answers \u201C${unanswered.title}\u201D.`}
              </p>
            )}
          </Fragment>
        );
      })}

      {!readOnly && (
        <button
          type="button"
          className="reminder-list__add"
          onClick={add}
          disabled={!canAdd}
        >
          <PlusOutlined />
          <span>
            {canAdd ? addLabel : `Limit of ${MAX_REMINDERS_PER_TASK} reminders reached`}
          </span>
        </button>
      )}
    </div>
  );
};

export default ReminderList;
